import { Link } from "react-router-dom";
import { ArrowRight, Users, Package, MapPin, Target } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { PROGRAMS } from "@/data/content";

const ProgramSection = ({ p, i }) => (
  <section className={`py-24 md:py-32 ${i === 1 ? "bg-white" : ""}`} data-testid={`program-${p.id}-section`} id={p.id}>
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center ${i % 2 ? "lg:[direction:rtl]" : ""}`}>
        <div className="lg:col-span-5 [direction:ltr] relative">
          <Reveal>
            <img src={p.img} alt={`${p.name} — ${p.stage}`} className="rounded-[2.5rem] w-full aspect-[4/5] object-cover" />
            <div
              className="absolute bottom-6 left-6 right-6 rounded-2xl backdrop-blur-xl bg-white/85 p-5 flex items-center gap-4"
            >
              <span className="font-display font-bold text-2xl w-12 h-12 rounded-full flex items-center justify-center text-white shrink-0" style={{ backgroundColor: p.color }}>
                {p.num}
              </span>
              <div>
                <p className="font-display font-semibold text-lg text-[#0B132B] leading-tight">{p.name}</p>
                <p className="text-sm text-[#4A5568]">{p.stage}</p>
              </div>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7 [direction:ltr]">
          <Reveal delay={0.1}>
            <p className="text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ color: p.color === "#0B132B" ? "#FF5A36" : p.color }}>
              Chapter {p.num} — {p.stage}
            </p>
            <h2 className="font-display font-semibold text-4xl md:text-5xl tracking-tight text-[#0B132B]">
              {p.name}
            </h2>
            <p className="font-display text-2xl text-[#FF5A36] mt-3 font-medium">{p.tagline}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
              <div className="bg-[#FAF8F5] border border-black/5 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-3 text-[#FF5A36]"><Users size={18} /><span className="text-xs uppercase tracking-[0.2em] font-bold">Who it's for</span></div>
                <p className="text-[#374151] text-sm leading-relaxed">{p.who}</p>
              </div>
              <div className="bg-[#FAF8F5] border border-black/5 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-3 text-[#FF5A36]"><Package size={18} /><span className="text-xs uppercase tracking-[0.2em] font-bold">What it delivers</span></div>
                <p className="text-[#374151] text-sm leading-relaxed">{p.delivers}</p>
              </div>
              <div className="bg-[#FAF8F5] border border-black/5 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-3 text-[#FF5A36]"><MapPin size={18} /><span className="text-xs uppercase tracking-[0.2em] font-bold">Format</span></div>
                <p className="text-[#374151] text-sm leading-relaxed">{p.format}</p>
              </div>
              <div className="bg-[#0B132B] rounded-2xl p-6 text-white">
                <div className="flex items-center gap-2 mb-3 text-[#FFB323]"><Target size={18} /><span className="text-xs uppercase tracking-[0.2em] font-bold">Outcomes</span></div>
                <ul className="space-y-2">
                  {p.outcomes.map((o) => (
                    <li key={o} className="text-sm text-white/85 leading-relaxed flex gap-2">
                      <span className="text-[#FF5A36] font-bold">→</span> {o}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

export default function Services() {
  return (
    <main data-testid="services-page">
      <section className="bg-[#0B132B] text-white pt-40 pb-24 md:pb-32 grain relative overflow-hidden" data-testid="services-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <Reveal>
            <Overline light>Our Programs</Overline>
            <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] max-w-4xl">
              One continuous journey: <span className="text-[#FFB323]">Inspire</span> →{" "}
              <span className="text-[#FF5A36]">Evolve</span> → <span className="text-white/70">Achieve</span>.
            </h1>
            <p className="text-white/70 max-w-2xl mt-8 text-lg leading-relaxed">
              Three signature programs that meet students where they are — Year 9 through to their
              first day at work — and walk beside them the whole way.
            </p>
          </Reveal>
        </div>
      </section>

      {PROGRAMS.map((p, i) => (
        <ProgramSection key={p.id} p={p} i={i} />
      ))}

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32" data-testid="services-identity-note">
        <Reveal>
          <div className="rounded-[2.5rem] bg-[#FFB323] p-12 md:p-16 relative overflow-hidden grain">
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-10 items-center">
              <div className="lg:col-span-2">
                <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#0B132B]/70 mb-4">Who this is for</p>
                <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight text-[#0B132B] leading-tight">
                  Culturally grounded. Identity-first. Never generic.
                </h2>
                <p className="mt-5 text-[#0B132B]/80 leading-relaxed max-w-2xl">
                  Every IBY program is designed for culturally diverse and disadvantaged students specifically —
                  built around culture, language and lived experience as strengths. If a program
                  could be run identically for any student anywhere, it isn't an IBY program.
                </p>
              </div>
              <div className="flex lg:justify-end">
                <Link
                  to="/contact"
                  data-testid="services-enquire-btn"
                  className="rounded-full bg-[#0B132B] text-white px-8 py-4 font-bold hover:bg-black transition-colors duration-300 flex items-center gap-2"
                >
                  Enquire for Your School <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
